import React from "react";

import { useNavigate } from "react-router-dom";

import useSlackIntegration from "@app/analytics/hooks/integrations/useSlackIntegration";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import type { MeetingReportDialog } from "@app/analytics/MeetingReport/types";
import { INTEGRATION_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import ROUTES from "@app/common/routes";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import { Link } from "@rui/Link";
import TooltipWrapper from "@rui/TooltipWrapper";

import { PushMenuItem } from "./PushMenuItem";
import ShareToSlackModal from "./ShareToSlackModal";
import { LogoIconButton, TooltipContent } from "./styles";
import type { ExternalShareProps } from "./types";

export interface ShareToSlackProps extends ExternalShareProps {
  className?: string;
}

const DisabledTooltipTitle = ({
  isConnected,
  canShareReport,
  onOpenDialog,
}: {
  isConnected: boolean;
  canShareReport: boolean;
  onOpenDialog: (dialog: MeetingReportDialog) => void;
}) => {
  if (!isConnected) {
    return (
      <TooltipContent>
        <Link
          color="shades.primary.200"
          to={ROUTES.INTEGRATIONS_USER_SLACK}
          underline
        >
          Enable Slack integration
        </Link>{" "}
        to share meeting notes to Slack
      </TooltipContent>
    );
  }

  if (!canShareReport) {
    return (
      <TooltipContent>
        Only report editors can post to Slack. To share,{" "}
        <Link
          color="shades.primary.200"
          onClick={(event) => {
            event.preventDefault();
            onOpenDialog("share");
          }}
          underline
        >
          request editor access
        </Link>{" "}
        from the meeting owner.
      </TooltipContent>
    );
  }

  return null;
};

const ShareToSlack = ({
  className,
  isMenuItem,
  canShareReport,
  inEditMode,
  isProcessing,
  sessionId,
}: ShareToSlackProps) => {
  const navigate = useNavigate();

  const { isConnected } = useSlackIntegration();
  const { workspaceId } = useWorkspace();

  const [dialog, setDialog] = useMeetingReportUiStore((state) => [
    state.dialog,
    state.setDialog,
  ]);

  const handleClickShareToSlack = () => {
    Events.shareToSlackClick();
    setDialog("post_to_slack");
  };

  const shareToSlackDisabled =
    !isConnected || !canShareReport || inEditMode || isProcessing;

  const iconButton = (
    <LogoIconButton
      alt={INTEGRATION_PROVIDERS_BY_ID.slack_bot.displayNameShort}
      iconSrc={INTEGRATION_PROVIDERS_BY_ID.slack_bot.icon}
      onClick={handleClickShareToSlack}
      disabled={shareToSlackDisabled}
    />
  );

  const menuItem = (
    <PushMenuItem
      disabled={shareToSlackDisabled}
      onClick={handleClickShareToSlack}
      imgSrc={INTEGRATION_PROVIDERS_BY_ID.slack_bot.icon}
      displayName={INTEGRATION_PROVIDERS_BY_ID.slack_bot.displayNameShort}
      isConnected={isConnected}
      onClickAdd={() => navigate(ROUTES.INTEGRATIONS_USER_SLACK)}
    />
  );

  return (
    <>
      <TooltipWrapper
        className={className}
        placement={isMenuItem ? "left" : "bottom"}
        show={!canShareReport || !isConnected}
        enterDelay={1500}
        title={
          <DisabledTooltipTitle
            canShareReport={canShareReport}
            isConnected={isConnected}
            onOpenDialog={setDialog}
          />
        }
      >
        {isMenuItem ? menuItem : iconButton}
      </TooltipWrapper>
      <ShareToSlackModal
        workspaceId={workspaceId}
        sessionId={sessionId!}
        open={dialog === "post_to_slack"}
        onClose={() => setDialog(null)}
      />
    </>
  );
};

export default ShareToSlack;
